import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import AppButton from "../../../../design-system/components/AppButton";
import AppCallout from "../../../../design-system/components/AppCallout";
import { IconArrowRight, IconChevronDown, IconChevronLeft, IconChevronRight, IconHoliday } from "../../../../design-system/icons";
import {
  addMonthsToDate,
  addWeeksToDate,
  parseDateValue,
  toIsoDate,
} from "../../domain/date/month";
import {
  baseShiftFilterId,
  buildPeriodCopyNotices,
  buildPeriodCopyPreview,
  CUSTOM_SHIFT_FILTER_ID,
  getPeriodCopyBounds,
  PERIOD_COPY_MODES,
} from "../../domain/model/periodCopy";
import usePlanRange from "../../domain/model/usePlanRange";
import ShiftplanDialog from "../ui/ShiftplanDialog";

/*
  Zwei Zeitraum-Listen zu einer machen, ohne dass ein Eintrag doppelt auftaucht.
  Bei sich überschneidenden Zeiträumen liefert der Server denselben Eintrag in
  beiden Antworten; doppelt kopiert würde er zweimal angelegt.
*/
function mergeById(first, second) {
  const merged = new Map();
  [first, second].forEach((rows) => {
    (Array.isArray(rows) ? rows : []).forEach((row) => {
      const key = String(row?.id ?? "");
      if (key && !merged.has(key)) merged.set(key, row);
    });
  });
  return Array.from(merged.values());
}

const DAY_MS = 86_400_000;
const MODE_OPTIONS = [
  { value: PERIOD_COPY_MODES.WEEK, label: "Woche" },
  { value: PERIOD_COPY_MODES.MONTH, label: "Monat" },
  { value: PERIOD_COPY_MODES.RANGE, label: "Zeitraum" },
];

function asText(value) {
  return String(value ?? "").trim();
}

function getShiftDateKey(shift) {
  return toIsoDate(shift?.start || shift?.dateKey);
}

function isShiftInRange(shift, startKey, endKey) {
  const dateKey = getShiftDateKey(shift);
  return Boolean(dateKey && dateKey >= startKey && dateKey <= endKey);
}

function optionCountText(count, singular, plural = `${singular}n`) {
  return count === 1 ? `1 ${singular}` : `${count} ${plural}`;
}

function sortOptions(left, right) {
  return String(left.title || "").localeCompare(String(right.title || ""), "de", { sensitivity: "base" });
}

function buildShiftTemplateMap(shiftTemplates) {
  const map = new Map();
  (Array.isArray(shiftTemplates) ? shiftTemplates : []).forEach((template) => {
    const id = baseShiftFilterId(template?.id);
    if (!id) return;
    const current = map.get(id) || {
      id,
      title: asText(template?.name) || "Schicht",
      shortName: asText(template?.shortName),
      timeLabel: asText(template?.timeLabel),
      departments: new Set(),
      color: asText(template?.color),
    };
    const department = asText(template?.department);
    if (department) current.departments.add(department);
    if (asText(template?.color)) current.color = asText(template.color);
    map.set(id, current);
  });
  return map;
}

function buildShiftFilterOptions({ shifts, shiftTemplates, sourceStartKey, sourceEndKey }) {
  const templatesById = buildShiftTemplateMap(shiftTemplates);
  const optionsById = new Map();

  (Array.isArray(shifts) ? shifts : [])
    .filter((shift) => shift?.entryType !== "absence" && isShiftInRange(shift, sourceStartKey, sourceEndKey))
    .forEach((shift) => {
      const templateId = baseShiftFilterId(shift?.shiftId);
      const id = templateId || CUSTOM_SHIFT_FILTER_ID;
      const template = templateId ? templatesById.get(templateId) : null;
      const current = optionsById.get(id) || {
        id,
        title:
          id === CUSTOM_SHIFT_FILTER_ID
            ? "Individuelle Schichten"
            : template?.title || asText(shift?.title) || asText(shift?.department) || "Schicht",
        shortName: template?.shortName || asText(shift?.titleShort),
        timeLabel: template?.timeLabel || asText(shift?.timeLabel),
        departments: new Set(template?.departments ? Array.from(template.departments) : []),
        color: template?.color || asText(shift?.color),
        count: 0,
      };
      const department = asText(shift?.department);
      if (department) current.departments.add(department);
      current.count += 1;
      optionsById.set(id, current);
    });

  return Array.from(optionsById.values())
    .map((option) => {
      const departments = Array.from(option.departments);
      const departmentText =
        departments.length > 1 ? optionCountText(departments.length, "Abteilung", "Abteilungen") : departments[0] || "";
      return {
        id: option.id,
        title: option.title,
        meta: [option.shortName, option.timeLabel, departmentText].filter(Boolean).join(" · "),
        count: option.count,
        color: option.color,
      };
    })
    .sort(sortOptions);
}

function addDays(value, amount) {
  const date = parseDateValue(value);
  if (!date) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount);
}

function dayDiff(left, right) {
  const leftDate = parseDateValue(left);
  const rightDate = parseDateValue(right);
  if (!leftDate || !rightDate) return 0;
  return Math.round((leftDate.getTime() - rightDate.getTime()) / DAY_MS);
}

function formatMonthInputValue(value) {
  const date = parseDateValue(value);
  if (!date) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function parseMonthInputValue(value) {
  const [year, month] = String(value || "").split("-").map(Number);
  if (!Number.isFinite(year) || !Number.isFinite(month)) return null;
  return new Date(year, month - 1, 1);
}

function formatDateLabel(value) {
  const date = parseDateValue(value);
  if (!date) return "";
  return new Intl.DateTimeFormat("de-CH", { weekday: "short", day: "2-digit", month: "2-digit" }).format(date);
}

function formatEntryCount(count) {
  return count === 1 ? "1 Eintrag" : `${count} Einträge`;
}

function getInitialCopyMode(viewMode) {
  return viewMode === PERIOD_COPY_MODES.WEEK ? PERIOD_COPY_MODES.WEEK : PERIOD_COPY_MODES.MONTH;
}

function getModeLabel(mode) {
  return MODE_OPTIONS.find((option) => option.value === mode)?.label || "Zeitraum";
}

function getSteppedDate(mode, value, amount) {
  return mode === PERIOD_COPY_MODES.WEEK ? addWeeksToDate(value, amount) : addMonthsToDate(value, amount);
}

function boundsToKeys(bounds) {
  return {
    startKey: toIsoDate(bounds?.start),
    endKey: toIsoDate(bounds?.end),
  };
}

function buildTargetRange(sourceStartKey, sourceEndKey, targetStartDate) {
  const targetStartKey = toIsoDate(targetStartDate);
  const span = Math.max(0, dayDiff(sourceEndKey, sourceStartKey));
  return {
    targetStartKey,
    targetEndKey: toIsoDate(addDays(targetStartKey, span)),
  };
}

function buildDefaultStateForMode(mode, viewMode, referenceDate) {
  if (mode === PERIOD_COPY_MODES.RANGE) {
    const baseMode = getInitialCopyMode(viewMode);
    const sourceBounds = getPeriodCopyBounds(baseMode, referenceDate);
    const sourceKeys = boundsToKeys(sourceBounds);
    const nextTargetStart = getSteppedDate(baseMode, sourceBounds.start, 1);
    const targetKeys = buildTargetRange(sourceKeys.startKey, sourceKeys.endKey, nextTargetStart);

    return {
      sourceReferenceDate: sourceBounds.start,
      targetReferenceDate: nextTargetStart,
      sourceStartKey: sourceKeys.startKey,
      sourceEndKey: sourceKeys.endKey,
      ...targetKeys,
    };
  }

  const sourceBounds = getPeriodCopyBounds(mode, referenceDate);
  const targetReferenceDate = getSteppedDate(mode, sourceBounds.start, 1);
  const targetBounds = getPeriodCopyBounds(mode, targetReferenceDate);
  const sourceKeys = boundsToKeys(sourceBounds);
  const targetKeys = boundsToKeys(targetBounds);

  return {
    sourceReferenceDate: sourceBounds.start,
    targetReferenceDate,
    sourceStartKey: sourceKeys.startKey,
    sourceEndKey: sourceKeys.endKey,
    targetStartKey: targetKeys.startKey,
    targetEndKey: targetKeys.endKey,
  };
}

function normalizeRangeChange(startKey, endKey) {
  if (!startKey) return { startKey: endKey || "", endKey: endKey || "" };
  if (!endKey) return { startKey, endKey: startKey };
  if (endKey < startKey) return { startKey: endKey, endKey: startKey };
  return { startKey, endKey };
}

function PeriodRangeInput({
  label,
  mode,
  referenceDate,
  bounds,
  startKey,
  endKey,
  onReferenceChange,
  onStep,
  onRangeChange,
}) {
  const isRangeMode = mode === PERIOD_COPY_MODES.RANGE;
  const isMonthMode = mode === PERIOD_COPY_MODES.MONTH;
  const periodLabel = getModeLabel(mode);
  const inputValue = isMonthMode ? formatMonthInputValue(referenceDate) : bounds.startKey;

  if (isRangeMode) {
    return (
      <Box className="spv2-period-copy-range-fields">
        <label className="spv2-period-copy-field">
          <span>Von</span>
          <input
            className="spv2-period-copy-input"
            type="date"
            value={startKey}
            onChange={(event) => onRangeChange(event.target.value, endKey)}
            aria-label={`${label} von`}
          />
        </label>
        <label className="spv2-period-copy-field">
          <span>Bis</span>
          <input
            className="spv2-period-copy-input"
            type="date"
            value={endKey}
            onChange={(event) => onRangeChange(startKey, event.target.value)}
            aria-label={`${label} bis`}
          />
        </label>
      </Box>
    );
  }

  return (
    <Box className="spv2-period-copy-target-row">
      <IconButton
        className="spv2-period-copy-step"
        size="small"
        onClick={() => onStep(-1)}
        aria-label={`${label}: vorherige ${periodLabel}`}
      >
        <IconChevronLeft fontSize="small" />
      </IconButton>
      <input
        className="spv2-period-copy-input"
        type={isMonthMode ? "month" : "date"}
        value={inputValue}
        onChange={(event) => {
          const nextDate = isMonthMode ? parseMonthInputValue(event.target.value) : parseDateValue(event.target.value);
          if (nextDate) onReferenceChange(nextDate);
        }}
        aria-label={`${label}-${periodLabel}`}
      />
      <IconButton
        className="spv2-period-copy-step"
        size="small"
        onClick={() => onStep(1)}
        aria-label={`${label}: nächste ${periodLabel}`}
      >
        <IconChevronRight fontSize="small" />
      </IconButton>
    </Box>
  );
}

function PreviewMetric({ value, label, icon, tone = "" }) {
  return (
    <Box className={`spv2-period-copy-metric${tone ? ` is-${tone}` : ""}`}>
      {icon ? <span className="spv2-period-copy-metric-icon">{icon}</span> : null}
      <Box className="spv2-period-copy-metric-copy">
        <Typography className="spv2-period-copy-metric-value">{value}</Typography>
        <Typography className="spv2-period-copy-metric-label">{label}</Typography>
      </Box>
    </Box>
  );
}

function selectedOptionIds(selectedIds, options) {
  const optionIds = new Set((Array.isArray(options) ? options : []).map((option) => String(option.id)));
  if (selectedIds == null) return Array.from(optionIds);
  return (Array.isArray(selectedIds) ? selectedIds : []).map(String).filter((id) => optionIds.has(id));
}

function filterSummary(selectedIds, options, allLabel, emptyLabel) {
  if (selectedIds == null) return allLabel;
  const selectedIdsInOptions = selectedOptionIds(selectedIds, options);
  if (!selectedIdsInOptions.length) return emptyLabel;
  if (selectedIdsInOptions.length === 1) {
    return options.find((option) => String(option.id) === selectedIdsInOptions[0])?.title || emptyLabel;
  }
  return `${selectedIdsInOptions.length} ausgewählt`;
}

function toggleFilterOption(selectedIds, options, optionId) {
  const optionIds = (Array.isArray(options) ? options : []).map((option) => String(option.id));
  const current = new Set(selectedIds == null ? optionIds : selectedOptionIds(selectedIds, options));
  const key = String(optionId);

  if (current.has(key)) {
    current.delete(key);
  } else {
    current.add(key);
  }

  const next = optionIds.filter((id) => current.has(id));
  return next.length === optionIds.length ? null : next;
}

function FilterDropdown({
  title,
  icon,
  options,
  selectedIds,
  onChange,
  allLabel,
  emptyLabel,
  emptyText,
  open,
  onOpen,
  onClose,
}) {
  const safeOptions = Array.isArray(options) ? options : [];
  const selectedIdsInOptions = selectedOptionIds(selectedIds, safeOptions);
  const isAll = selectedIds == null;
  const isPartial = !isAll && selectedIdsInOptions.length > 0 && selectedIdsInOptions.length < safeOptions.length;
  const selectedSet = new Set(selectedIdsInOptions);
  const summary = filterSummary(selectedIds, safeOptions, allLabel, emptyLabel);

  return (
    <Box className={`spv2-period-copy-filter${open ? " is-open" : ""}${icon ? " has-icon" : ""}`}>
      <button
        type="button"
        className="spv2-period-copy-filter-trigger"
        aria-label={`${title}: ${summary}`}
        aria-expanded={open}
        onClick={() => (open ? onClose() : onOpen())}
      >
        {icon ? (
          <span className="spv2-period-copy-filter-trigger-icon" aria-hidden="true">
            {icon}
          </span>
        ) : null}
        <span className="spv2-period-copy-filter-trigger-copy">
          <strong>{summary}</strong>
        </span>
        <IconChevronDown fontSize="small" />
      </button>

      {open ? (
        <Box className="spv2-period-copy-filter-menu" role="menu">
          <label className="spv2-period-copy-filter-row is-all">
            <Checkbox
              size="small"
              checked={isAll}
              indeterminate={isPartial}
              onChange={() => onChange(isAll ? [] : null)}
              inputProps={{ "aria-label": allLabel }}
            />
            <Box className="spv2-period-copy-filter-row-copy">
              <span>{allLabel}</span>
              <em>{optionCountText(safeOptions.length, "Option", "Optionen")}</em>
            </Box>
          </label>

          {safeOptions.length ? (
            <Box className="spv2-period-copy-filter-list">
              {safeOptions.map((option) => {
                const checked = isAll || selectedSet.has(String(option.id));
                return (
                  <label key={option.id} className={`spv2-period-copy-filter-row${checked ? " is-selected" : ""}`}>
                    <Checkbox
                      size="small"
                      checked={checked}
                      onChange={() => onChange(toggleFilterOption(selectedIds, safeOptions, option.id))}
                      inputProps={{ "aria-label": `${option.title} auswählen` }}
                    />
                    <span
                      className={`spv2-period-copy-filter-swatch${option.color ? "" : " is-empty"}`}
                      style={option.color ? { "--spv2-filter-color": option.color } : undefined}
                      aria-hidden="true"
                    />
                    <Box className="spv2-period-copy-filter-row-copy">
                      <span>{option.title}</span>
                      {option.meta ? <em>{option.meta}</em> : null}
                    </Box>
                  </label>
                );
              })}
            </Box>
          ) : (
            <Box className="spv2-period-copy-filter-empty">{emptyText}</Box>
          )}
        </Box>
      ) : null}
    </Box>
  );
}

function StepSection({ children, className = "" }) {
  return (
    <Box className={`spv2-period-copy-step-card${className ? ` ${className}` : ""}`}>
      {children}
    </Box>
  );
}

export default function PeriodCopyPanel({
  open = false,
  viewMode = PERIOD_COPY_MODES.MONTH,
  referenceDate,
  shifts = [],
  userHolidays = [],
  employees = [],
  shiftTemplates = [],
  locationName = "",
  isSaving = false,
  onLoadPlanRange,
  onClose,
  onCopy,
}) {
  const initialMode = getInitialCopyMode(viewMode);
  const initialState = buildDefaultStateForMode(initialMode, viewMode, referenceDate);
  const [copyMode, setCopyMode] = useState(initialMode);
  const [sourceReferenceDate, setSourceReferenceDate] = useState(initialState.sourceReferenceDate);
  const [targetReferenceDate, setTargetReferenceDate] = useState(initialState.targetReferenceDate);
  const [sourceStartKey, setSourceStartKey] = useState(initialState.sourceStartKey);
  const [sourceEndKey, setSourceEndKey] = useState(initialState.sourceEndKey);
  const [targetStartKey, setTargetStartKey] = useState(initialState.targetStartKey);
  const [targetEndKey, setTargetEndKey] = useState(initialState.targetEndKey);
  const [includeAbsences, setIncludeAbsences] = useState(false);
  const [includeHolidays, setIncludeHolidays] = useState(false);
  const [selectedShiftIds, setSelectedShiftIds] = useState(null);
  const [openFilter, setOpenFilter] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setConfirmOpen(false);
      return;
    }

    const nextMode = getInitialCopyMode(viewMode);
    const defaults = buildDefaultStateForMode(nextMode, viewMode, referenceDate);
    setCopyMode(nextMode);
    setSourceReferenceDate(defaults.sourceReferenceDate);
    setTargetReferenceDate(defaults.targetReferenceDate);
    setSourceStartKey(defaults.sourceStartKey);
    setSourceEndKey(defaults.sourceEndKey);
    setTargetStartKey(defaults.targetStartKey);
    setTargetEndKey(defaults.targetEndKey);
    setIncludeAbsences(false);
    setIncludeHolidays(false);
    setSelectedShiftIds(null);
    setOpenFilter("");
  }, [open, referenceDate, viewMode]);

  const isRangeMode = copyMode === PERIOD_COPY_MODES.RANGE;
  const sourceBounds = useMemo(
    () =>
      getPeriodCopyBounds(copyMode, sourceReferenceDate, {
        startDate: sourceStartKey,
        endDate: sourceEndKey,
      }),
    [copyMode, sourceEndKey, sourceReferenceDate, sourceStartKey]
  );
  const targetBounds = useMemo(
    () =>
      getPeriodCopyBounds(copyMode, targetReferenceDate, {
        startDate: targetStartKey,
        endDate: targetEndKey,
      }),
    [copyMode, targetEndKey, targetReferenceDate, targetStartKey]
  );
  const sourceKeys = boundsToKeys(sourceBounds);
  const targetKeys = boundsToKeys(targetBounds);

  /*
    Zwei Zeiträume, nicht einer.

    Die Quelle liegt fast nie im Sichtbereich — man kopiert ja aus einem anderen
    Monat. Das Ziel wird aber genauso gebraucht: Die Vorschau markiert Einträge,
    die es dort schon gibt ("Duplikat"), und dafür muss sie wissen, was dort
    steht. Kennt sie nur die Quelle, meldet sie nie ein Duplikat und man
    überschreibt einen bereits geplanten Monat, ohne gewarnt zu werden.
  */
  const sourceRange = usePlanRange({
    enabled: open,
    startDate: sourceKeys.startKey,
    endDate: sourceKeys.endKey,
    onLoadPlanRange,
    fallbackShifts: shifts,
    fallbackUserHolidays: userHolidays,
  });
  const targetRange = usePlanRange({
    enabled: open,
    startDate: targetKeys.startKey,
    endDate: targetKeys.endKey,
    onLoadPlanRange,
    fallbackShifts: shifts,
    fallbackUserHolidays: userHolidays,
  });

  /*
    buildPeriodCopyPreview nimmt eine Liste und liest daraus beides: die zu
    kopierenden Einträge (nach Quelldatum gefiltert) und den Bestand für die
    Duplikatsprüfung. Beide Zeiträume kommen deshalb zusammen hinein — nach ID
    entdoppelt, damit ein Eintrag nicht zweimal kopiert wird, falls sich Quelle
    und Ziel überschneiden.
  */
  const sourceShifts = useMemo(
    () => mergeById(sourceRange.shifts, targetRange.shifts),
    [sourceRange.shifts, targetRange.shifts]
  );
  const sourceUserHolidays = useMemo(
    () => mergeById(sourceRange.userHolidays, targetRange.userHolidays),
    [sourceRange.userHolidays, targetRange.userHolidays]
  );
  const isRangeLoading = sourceRange.isLoading || targetRange.isLoading;

  const shiftFilterOptions = useMemo(
    () =>
      buildShiftFilterOptions({
        shifts: sourceShifts,
        shiftTemplates,
        sourceStartKey: sourceKeys.startKey,
        sourceEndKey: sourceKeys.endKey,
      }),
    [shiftTemplates, sourceShifts, sourceKeys.endKey, sourceKeys.startKey]
  );
  const preview = useMemo(
    () =>
      buildPeriodCopyPreview({
        periodMode: copyMode,
        sourceReferenceDate: isRangeMode ? undefined : sourceReferenceDate,
        targetReferenceDate: isRangeMode ? undefined : targetReferenceDate,
        sourceStartDate: isRangeMode ? sourceStartKey : undefined,
        sourceEndDate: isRangeMode ? sourceEndKey : undefined,
        targetStartDate: isRangeMode ? targetStartKey : undefined,
        targetEndDate: isRangeMode ? targetEndKey : undefined,
        shifts: sourceShifts,
        userHolidays: sourceUserHolidays,
        employees,
        includeAbsences,
        includeHolidays,
        selectedShiftIds,
      }),
    [
      copyMode,
      selectedShiftIds,
      employees,
      includeAbsences,
      includeHolidays,
      isRangeMode,
      sourceShifts,
      sourceUserHolidays,
      sourceEndKey,
      sourceReferenceDate,
      sourceStartKey,
      targetEndKey,
      targetReferenceDate,
      targetStartKey,
    ]
  );

  const notices = useMemo(() => buildPeriodCopyNotices(preview), [preview]);

  // Nicht kopieren, solange die Zeiträume noch geladen werden — oder ihr Laden
  // fehlgeschlagen ist: usePlanRange fällt dann still auf die angezeigte
  // Periode zurück, die Duplikatsprüfung liefe gegen den falschen Monat und
  // ein bereits geplanter Monat ließe sich unbemerkt doppelt befüllen.
  const hasRangeError = Boolean(sourceRange.error || targetRange.error);
  const canCopy = preview.events.length > 0 && !isSaving && !isRangeLoading && !hasRangeError;
  const panelTitle = `${getModeLabel(copyMode)} kopieren`;
  const rangeLabel = `${formatDateLabel(preview.source.startKey)} - ${formatDateLabel(preview.source.endKey)}`;
  const targetRangeLabel = `${formatDateLabel(preview.target.startKey)} - ${formatDateLabel(preview.target.endKey)}`;

  const applyModeDefaults = (nextMode) => {
    const defaults = buildDefaultStateForMode(nextMode, viewMode, referenceDate);
    setCopyMode(nextMode);
    setSourceReferenceDate(defaults.sourceReferenceDate);
    setTargetReferenceDate(defaults.targetReferenceDate);
    setSourceStartKey(defaults.sourceStartKey);
    setSourceEndKey(defaults.sourceEndKey);
    setTargetStartKey(defaults.targetStartKey);
    setTargetEndKey(defaults.targetEndKey);
    setOpenFilter("");
    setConfirmOpen(false);
  };

  const handleReferenceStep = (scope, amount) => {
    if (isRangeMode) return;
    const setter = scope === "source" ? setSourceReferenceDate : setTargetReferenceDate;
    setter((current) => getSteppedDate(copyMode, current, amount));
  };

  const handleRangeChange = (scope, nextStartKey, nextEndKey) => {
    const normalized = normalizeRangeChange(nextStartKey, nextEndKey);
    if (scope === "source") {
      setSourceStartKey(normalized.startKey);
      setSourceEndKey(normalized.endKey);
      return;
    }
    setTargetStartKey(normalized.startKey);
    setTargetEndKey(normalized.endKey);
  };

  const handleCopyRequest = () => {
    if (!canCopy) return;
    setConfirmOpen(true);
  };

  const handleConfirmCopy = async () => {
    if (!canCopy) return;

    try {
      await onCopy?.(preview);
      setConfirmOpen(false);
      onClose?.();
    } catch (_error) {
      // The layout already shows the API error. Keep the dialog open so the user can retry.
    }
  };

  return (
    <>
      <ShiftplanDialog
        open={open}
        title={panelTitle}
        subtitle={locationName}
        icon={<ContentCopyRoundedIcon fontSize="small" />}
        size="lg"
        bodyClassName="spv2-period-copy-dialog-body"
        footerStart={
          <Typography className="spv2-period-copy-footer-target">Ziel: {preview.target.label}</Typography>
        }
        footerActions={
          <AppButton variant="primary" onClick={handleCopyRequest} disabled={!canCopy}>
            <ContentCopyRoundedIcon fontSize="small" />
            {isSaving ? "Kopieren..." : `${formatEntryCount(preview.events.length)} kopieren`}
          </AppButton>
        }
        onClose={onClose}
      >
        <Box className="spv2-period-copy-panel">
          <StepSection className="is-mode">
            <Box className="spv2-order-mode-switch spv2-period-copy-mode" role="group" aria-label="Kopiermodus">
              {MODE_OPTIONS.map((option) => (
                <button
                  type="button"
                  key={option.value}
                  className={`spv2-order-mode-btn spv2-period-copy-mode-btn${
                    copyMode === option.value ? " is-active" : ""
                  }`}
                  aria-pressed={copyMode === option.value}
                  onClick={() => applyModeDefaults(option.value)}
                >
                  <span>{option.label}</span>
                </button>
              ))}
            </Box>
          </StepSection>

          <StepSection className="is-periods">
            <Box className="spv2-period-copy-periods">
              <Box className="spv2-period-copy-period">
                <Typography className="spv2-period-copy-label">Quelle</Typography>
                <Typography className="spv2-period-copy-period-title">{preview.source.label}</Typography>
                <Typography className="spv2-period-copy-period-range">{rangeLabel}</Typography>
                <PeriodRangeInput
                  label="Quelle"
                  mode={copyMode}
                  referenceDate={sourceReferenceDate}
                  bounds={sourceKeys}
                  startKey={sourceStartKey}
                  endKey={sourceEndKey}
                  onReferenceChange={setSourceReferenceDate}
                  onStep={(amount) => handleReferenceStep("source", amount)}
                  onRangeChange={(startKey, endKey) => handleRangeChange("source", startKey, endKey)}
                />
              </Box>

              <Box className="spv2-period-copy-period-arrow" aria-hidden="true">
                <IconArrowRight fontSize="small" />
              </Box>

              <Box className="spv2-period-copy-period is-target">
                <Typography className="spv2-period-copy-label">Ziel</Typography>
                <Typography className="spv2-period-copy-period-title">{preview.target.label}</Typography>
                <Typography className="spv2-period-copy-period-range">{targetRangeLabel}</Typography>
                <PeriodRangeInput
                  label="Ziel"
                  mode={copyMode}
                  referenceDate={targetReferenceDate}
                  bounds={targetKeys}
                  startKey={targetStartKey}
                  endKey={targetEndKey}
                  onReferenceChange={setTargetReferenceDate}
                  onStep={(amount) => handleReferenceStep("target", amount)}
                  onRangeChange={(startKey, endKey) => handleRangeChange("target", startKey, endKey)}
                />
              </Box>
            </Box>
          </StepSection>

          <StepSection className="is-content">
            <Box className="spv2-period-copy-options">
              <Box className="spv2-period-copy-option-group">
                <span className="spv2-period-copy-group-label">
                  <WorkOutlineOutlinedIcon fontSize="inherit" />
                  <span>Schichten</span>
                </span>
                <ClickAwayListener onClickAway={() => setOpenFilter("")}>
                  <Box className="spv2-period-copy-shift-filters">
                    <FilterDropdown
                      title="Schichten"
                      options={shiftFilterOptions}
                      selectedIds={selectedShiftIds}
                      onChange={setSelectedShiftIds}
                      allLabel="Alle Schichten"
                      emptyLabel="Keine Schicht"
                      emptyText="Keine Schichten im Quellzeitraum."
                      open={openFilter === "shifts"}
                      onOpen={() => setOpenFilter("shifts")}
                      onClose={() => setOpenFilter("")}
                    />
                  </Box>
                </ClickAwayListener>
              </Box>

              {/*
                Kästchen statt Schalter: hier wird ausgewählt, was mitgeht –
                das ist ein Häkchen, keine Ein/Aus-Einstellung.
              */}
              <Box className="spv2-period-copy-option-group">
                <span className="spv2-period-copy-group-label">
                  <ContentCopyRoundedIcon fontSize="inherit" />
                  <span>Mitkopieren</span>
                </span>
                <Box className="spv2-period-copy-include-list">
                  <label className={`spv2-period-copy-include${includeAbsences ? " is-active" : ""}`}>
                    <Checkbox
                      size="small"
                      className="spv2-period-copy-include-check"
                      checked={includeAbsences}
                      onChange={(event) => setIncludeAbsences(event.target.checked)}
                      inputProps={{ "aria-label": "Absenzen mitkopieren" }}
                    />
                    <span className="spv2-period-copy-include-icon" aria-hidden="true">
                      <EventBusyOutlinedIcon fontSize="inherit" />
                    </span>
                    <span className="spv2-period-copy-include-name">Absenzen</span>
                  </label>
                  <label className={`spv2-period-copy-include${includeHolidays ? " is-active" : ""}`}>
                    <Checkbox
                      size="small"
                      className="spv2-period-copy-include-check"
                      checked={includeHolidays}
                      onChange={(event) => setIncludeHolidays(event.target.checked)}
                      inputProps={{ "aria-label": "Ferien mitkopieren" }}
                    />
                    <span className="spv2-period-copy-include-icon is-vacation" aria-hidden="true">
                      <IconHoliday fontSize="inherit" />
                    </span>
                    <span className="spv2-period-copy-include-name">Ferien</span>
                  </label>
                </Box>
              </Box>
            </Box>
          </StepSection>

          <StepSection className="is-final">
            <Box className="spv2-period-copy-metrics" aria-label="Kopierzusammenfassung">
              <PreviewMetric
                value={preview.counts.shifts}
                label="Schichten"
                icon={<WorkOutlineOutlinedIcon fontSize="small" />}
                tone="shifts"
              />
              <PreviewMetric
                value={preview.counts.absences}
                label="Absenzen"
                icon={<EventBusyOutlinedIcon fontSize="small" />}
                tone="absences"
              />
              <PreviewMetric
                value={preview.counts.holidays}
                label="Ferien"
                icon={<IconHoliday fontSize="small" />}
                tone="vacation"
              />
              <PreviewMetric
                value={preview.counts.total}
                label="werden kopiert"
                icon={<ContentCopyRoundedIcon fontSize="small" />}
                tone="accent"
              />
            </Box>

            {hasRangeError ? (
              <Box className="spv2-period-copy-notices">
                <AppCallout
                  variant="error"
                  title="Zeitraum konnte nicht geladen werden"
                  description="Ohne die Daten von Quelle und Ziel kann die Vorschau Duplikate nicht erkennen, deshalb ist Kopieren gerade gesperrt. Prüfe deine Verbindung und öffne das Panel erneut."
                />
              </Box>
            ) : null}

            {notices.length ? (
              <Box className="spv2-period-copy-notices">
                {notices.map((notice) => (
                  <AppCallout
                    key={notice.id}
                    variant={notice.variant}
                    title={notice.title}
                    description={notice.description}
                  />
                ))}
              </Box>
            ) : null}

            {!preview.events.length ? (
              <Box className="spv2-period-copy-empty">Im Quellzeitraum gibt es keine passenden Einträge.</Box>
            ) : null}

          </StepSection>
        </Box>
      </ShiftplanDialog>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        className="spv2-period-copy-confirm-dialog"
      >
        <DialogTitle className="spv2-period-copy-confirm-title">Zeitraum kopieren?</DialogTitle>
        <DialogContent className="spv2-period-copy-confirm-body">
          <Box className="spv2-period-copy-confirm-hero">
            <span className="spv2-period-copy-confirm-hero-icon">
              <ContentCopyRoundedIcon fontSize="small" />
            </span>
            <Box>
              <Typography className="spv2-period-copy-confirm-kicker">Bereit zum Kopieren</Typography>
              <Typography className="spv2-period-copy-confirm-count">
                {formatEntryCount(preview.events.length)}
              </Typography>
            </Box>
          </Box>

          <Box className="spv2-period-copy-confirm-route" aria-label="Kopierweg">
            <Box className="spv2-period-copy-confirm-side">
              <span>Quelle</span>
              <strong>{preview.source.label}</strong>
              <em>{rangeLabel}</em>
            </Box>
            <Box className="spv2-period-copy-confirm-arrow" aria-hidden="true">
              <IconArrowRight fontSize="small" />
            </Box>
            <Box className="spv2-period-copy-confirm-side is-target">
              <span>Ziel</span>
              <strong>{preview.target.label}</strong>
              <em>{targetRangeLabel}</em>
            </Box>
          </Box>

          <Box className="spv2-period-copy-confirm-metrics" aria-label="Inhalt">
            <span>
              <WorkOutlineOutlinedIcon fontSize="small" />
              {preview.counts.shifts} Schichten
            </span>
            <span>
              <EventBusyOutlinedIcon fontSize="small" />
              {preview.counts.absences} Absenzen
            </span>
            <span>
              <IconHoliday fontSize="small" />
              {preview.counts.holidays} Ferien
            </span>
          </Box>
        </DialogContent>
        <DialogActions className="spv2-period-copy-confirm-actions">
          <Button onClick={() => setConfirmOpen(false)} disabled={isSaving}>
            Zurück
          </Button>
          <Button
            variant="contained"
            startIcon={<ContentCopyRoundedIcon fontSize="small" />}
            onClick={handleConfirmCopy}
            disabled={!canCopy}
            autoFocus
          >
            Jetzt {formatEntryCount(preview.events.length)} kopieren
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
